import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { Trophy, Target, Star } from 'lucide-react';
import { playerService } from '../services/playerService';
import { useToast } from '../hooks/use-toast';

const CATEGORIES = [
  { value: 'attempts', label: 'Most Active', icon: Target },
  { value: 'average_score', label: 'Highest Avg Score', icon: Star },
  { value: 'highest_score', label: 'Highest Score', icon: Trophy },
];

const Leaderboard = () => {
  const { toast } = useToast();
  const [category, setCategory] = useState('attempts');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const data = await playerService.getLeaderboard({ category });
        /* istanbul ignore else -- cancellation guard for a stale success response; unreachable via UI
           because the category buttons are hidden while any fetch is pending (no way to supersede a
           fetch before it settles), and React 19 no longer warns on state updates after unmount, so
           the skipped branch has no way to be observed even via an unmount-based test */
        if (!cancelled) setLeaderboard(data);
      } catch (error) {
        if (!cancelled) {
          toast({ title: 'Error', description: 'Failed to load leaderboard', variant: 'destructive' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLeaderboard();
    return () => { cancelled = true; };
  }, [category, toast]);

  if (loading) {
    return (
      <Layout>
        <SEOHead
          title="Leaderboard - BookQuiz"
          description="See who's leading the pack. Top BookQuiz players ranked by activity and score."
          url="/leaderboard"
        />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title="Leaderboard - BookQuiz"
        description="See who's leading the pack. Top BookQuiz players ranked by activity and score."
        url="/leaderboard"
      />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 flex items-center gap-3">
          <Trophy className="h-8 w-8" /> Leaderboard
        </h1>

        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                category === cat.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <cat.icon className="h-4 w-4 inline mr-2" />
              {cat.label}
            </button>
          ))}
        </div>

        {leaderboard.length === 0 ? (
          <Card><CardContent className="pt-12 text-center">
            <Trophy className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No results yet. Be the first to take a quiz!</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((entry, index) => (
              <Card key={entry.player_id}>
                <CardContent className="pt-6 flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center font-bold text-lg">
                    #{index + 1}
                  </div>
                  <Link to={`/players/${entry.player_id}`} className="flex-1 hover:text-blue-600">
                    <div className="font-semibold">{entry.name}</div>
                  </Link>
                  <div className="text-right">
                    <div className="font-bold">{entry.total_attempts}</div>
                    <div className="text-xs text-gray-500">Quizzes</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{entry.average_score}%</div>
                    <div className="text-xs text-gray-500">Avg Score</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{entry.highest_score}%</div>
                    <div className="text-xs text-gray-500">Best</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Leaderboard;
